import { useCallback, useMemo, useState } from 'react';
import { updateEventMembersVisibility } from '../../../api/taskEvents';
import { useToast } from '../ToastSystem';

const resolveMembersVisibilityEventLabel = (event, fallback = '公演') => {
  const performance = String(event?.performance_type || '').trim();
  if (performance) return performance;
  const name = String(event?.name || '').trim();
  if (name) return name;
  return fallback;
};

const normalizeMembersVisibleToArtists = (event) =>
  event?.members_visible_to_artists === true || event?.membersVisibleToArtists === true;

export default function useEventMembersVisibilityControl({
  eventId = '',
  currentEvent = null,
  groupEventRows = [],
  hasGroupEvents = false,
  canEditParticipants = false,
  readOnlyTitle = 'このユーザー権限ではこの操作を実行できません。',
  setCurrentEventState = null,
  setGroupEventsState = null,
}) {
  const { error: toastError, success: toastSuccess } = useToast();
  const [membersVisibilitySaving, setMembersVisibilitySaving] = useState(false);
  const [membersVisibilityConfirmState, setMembersVisibilityConfirmState] = useState({
    open: false,
    nextVisible: true,
    showGroupChoices: false,
    unpublishedEventLabels: [],
    currentEventLabel: '',
  });

  const membersVisibleToArtists = useMemo(
    () => normalizeMembersVisibleToArtists(currentEvent),
    [currentEvent]
  );
  const membersVisibilityButtonLabel = membersVisibleToArtists ? '公開中' : '非公開';
  const membersVisibilityButtonClass = membersVisibleToArtists ? 'btn-primary btn--sm' : 'btn-outline btn--sm';

  const closeMembersVisibilityConfirmModal = useCallback(() => {
    if (membersVisibilitySaving) return;
    setMembersVisibilityConfirmState({
      open: false,
      nextVisible: true,
      showGroupChoices: false,
      unpublishedEventLabels: [],
      currentEventLabel: '',
    });
  }, [membersVisibilitySaving]);

  const updateCurrentEventVisibility = useCallback((updatedVisible) => {
    if (typeof setCurrentEventState !== 'function') return;
    setCurrentEventState((prev) =>
      prev
        ? {
            ...prev,
            members_visible_to_artists: updatedVisible,
            membersVisibleToArtists: updatedVisible,
          }
        : prev
    );
  }, [setCurrentEventState]);

  const updateGroupEventsVisibility = useCallback(({ publishedEventUuids = [], nextVisible }) => {
    if (typeof setGroupEventsState !== 'function') return;
    const publishedSet = new Set(
      Array.isArray(publishedEventUuids) ? publishedEventUuids.map((value) => String(value)) : []
    );
    setGroupEventsState((prev) => {
      const base = Array.isArray(prev) && prev.length > 0
        ? prev
        : (Array.isArray(groupEventRows) ? groupEventRows : []);
      if (!Array.isArray(base) || base.length === 0) return prev;

      if (publishedSet.size > 0) {
        return base.map((event) => {
          const eventUuid = String(event?.event_uuid || '');
          if (!eventUuid || !publishedSet.has(eventUuid)) return event;
          return {
            ...event,
            members_visible_to_artists: true,
            membersVisibleToArtists: true,
          };
        });
      }

      if (!nextVisible) {
        return base.map((event) => {
          const eventUuid = String(event?.event_uuid || '');
          if (!eventUuid || eventUuid !== String(eventId)) return event;
          return {
            ...event,
            members_visible_to_artists: false,
            membersVisibleToArtists: false,
          };
        });
      }

      return base;
    });
  }, [eventId, groupEventRows, setGroupEventsState]);

  const submitMembersVisibilityChange = useCallback(async ({ nextVisible, publishScope = 'single' }) => {
    if (!eventId || membersVisibilitySaving) return;
    setMembersVisibilitySaving(true);
    try {
      const data = await updateEventMembersVisibility(eventId, {
        visible: nextVisible,
        publish_scope: publishScope,
      });
      const updatedVisible =
        typeof data?.event?.members_visible_to_artists === 'boolean'
          ? data.event.members_visible_to_artists
          : nextVisible;

      updateCurrentEventVisibility(updatedVisible);
      updateGroupEventsVisibility({
        publishedEventUuids: Array.isArray(data?.published_event_uuids) ? data.published_event_uuids : [],
        nextVisible,
      });

      if (!nextVisible) {
        toastSuccess('参加アーティストを非公開にしました');
      } else if (Array.isArray(data?.published_event_names) && data.published_event_names.length > 0) {
        if (
          data?.publish_scope === 'group_all' &&
          Number(data?.group_event_count || 0) > 1 &&
          data.published_event_names.length === Number(data.group_event_count || 0)
        ) {
          toastSuccess('全公演の参加アーティストを公開しました');
        } else {
          toastSuccess(`${data.published_event_names.join(',')} の参加アーティストを公開しました`);
        }
      } else {
        toastSuccess(updatedVisible ? '参加アーティストを公開しました' : '参加アーティストを非公開にしました');
      }
    } catch (error) {
      console.error('handleMembersVisibilityToggle error:', error);
      toastError(error.message || '参加アーティストの公開設定に失敗しました');
    } finally {
      setMembersVisibilitySaving(false);
    }
  }, [
    eventId,
    membersVisibilitySaving,
    toastError,
    toastSuccess,
    updateCurrentEventVisibility,
    updateGroupEventsVisibility,
  ]);

  const handleMembersVisibilityConfirmSingle = useCallback(async () => {
    const nextVisible = membersVisibilityConfirmState.nextVisible;
    setMembersVisibilityConfirmState({
      open: false,
      nextVisible: true,
      showGroupChoices: false,
      unpublishedEventLabels: [],
      currentEventLabel: '',
    });
    await submitMembersVisibilityChange({ nextVisible, publishScope: 'single' });
  }, [membersVisibilityConfirmState.nextVisible, submitMembersVisibilityChange]);

  const handleMembersVisibilityConfirmGroupAll = useCallback(async () => {
    setMembersVisibilityConfirmState({
      open: false,
      nextVisible: true,
      showGroupChoices: false,
      unpublishedEventLabels: [],
      currentEventLabel: '',
    });
    await submitMembersVisibilityChange({ nextVisible: true, publishScope: 'group_all' });
  }, [submitMembersVisibilityChange]);

  const handleMembersVisibilityToggle = useCallback(async () => {
    if (!eventId || membersVisibilitySaving) return;
    if (!canEditParticipants) {
      toastError(readOnlyTitle);
      return;
    }

    const nextVisible = !membersVisibleToArtists;
    const showGroupChoices = nextVisible && hasGroupEvents && groupEventRows.length > 1;
    const unpublishedEventLabels = showGroupChoices
      ? groupEventRows
          .filter((event) => normalizeMembersVisibleToArtists(event) !== true)
          .map((event, index) => resolveMembersVisibilityEventLabel(event, `公演${index + 1}`))
      : [];
    const currentGroupEvent = groupEventRows.find(
      (event) => String(event?.event_uuid || '') === String(eventId)
    );
    setMembersVisibilityConfirmState({
      open: true,
      nextVisible,
      showGroupChoices,
      unpublishedEventLabels,
      currentEventLabel: resolveMembersVisibilityEventLabel(currentGroupEvent || currentEvent, 'この公演'),
    });
  }, [
    canEditParticipants,
    currentEvent,
    eventId,
    groupEventRows,
    hasGroupEvents,
    membersVisibilitySaving,
    membersVisibleToArtists,
    readOnlyTitle,
    toastError,
  ]);

  return {
    membersVisibleToArtists,
    membersVisibilitySaving,
    membersVisibilityButtonLabel,
    membersVisibilityButtonClass,
    membersVisibilityConfirmState,
    closeMembersVisibilityConfirmModal,
    handleMembersVisibilityConfirmSingle,
    handleMembersVisibilityConfirmGroupAll,
    handleMembersVisibilityToggle,
  };
}
